import { z } from 'zod';

import {
  VIDEO_ID_REGEX,
  YOUTUBE_EMBED_REGEX,
  YOUTUBE_REGEX,
} from 'utils/story/regex';

export const marksOptions = z.enum(['bold', 'strike', 'italic']);

export const storyMarksSchema = z
  .array(z.object({ type: marksOptions }))
  .nonempty()
  .optional();

export const stripLineBreaks = (text: string) => {
  return text.replace(/(\r\n|\n|\r)/gm, '');
};

export const storyBlockTypeOptions = z.enum([
  'PARAGRAPH',
  'H1',
  'H2',
  'BLOCKQUOTE',
  'VIDEO',
  'IMAGE',
  'NFT',
  'COLLECTION',
  'INVENTORY_ITEM',
]);

const textSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});

const singleLineTextSchema = z.object({
  type: z.literal('text'),
  text: z.string().transform(stripLineBreaks),
});

export const textSchemaWithMarks = textSchema.extend({
  marks: storyMarksSchema,
});

const zodFilteredArray = <S>(itemSchema: z.ZodType<S>) => {
  const catchValue = {} as never;
  return z.array(itemSchema.catch(catchValue)).transform((a, ctx) => {
    const filteredArray = a.filter((o) => o !== catchValue);
    if (filteredArray.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Content cannot be empty',
      });
      return z.NEVER;
    }
    return filteredArray;
  });
};

export const storyParagraphSchema = z.object({
  type: z.literal('doc'),
  content: zodFilteredArray(
    z.object({
      type: z.literal('paragraph'),
      content: z.array(textSchemaWithMarks),
    })
  ),
});

const headingContentSchema = z.object({
  type: z.literal('heading'),
  content: z.tuple([singleLineTextSchema.strict()]),
});

export const storyH1Schema = z.object({
  type: z.literal('doc'),
  content: z.tuple([
    headingContentSchema.extend({
      attrs: z
        .object({
          level: z.literal(1),
        })
        .strict(),
    }),
  ]),
});

export const storyH2Schema = z.object({
  type: z.literal('doc'),
  content: z.tuple([
    headingContentSchema.extend({
      attrs: z
        .object({
          level: z.literal(2),
        })
        .strict(),
    }),
  ]),
});

const storyBlockquoteParagraphSchema = z.object({
  type: z.literal('paragraph'),
  content: z.tuple([singleLineTextSchema.strict()]),
});

const storyBlockquoteFooterSchema = z.object({
  type: z.literal('footer'),
  content: z.tuple([
    z.object({
      type: z.literal('cite'),
      content: z.tuple([singleLineTextSchema.strict()]),
    }),
  ]),
});

export const storyBlockquoteSchema = z.object({
  type: z.literal('doc'),
  content: z.tuple([
    z.object({
      type: z.literal('blockquote'),
      content: z.tuple([
        storyBlockquoteParagraphSchema,
        storyBlockquoteFooterSchema,
      ]),
    }),
  ]),
});

/**
 * IMAGE type
 */

const figureImageSchema = z.object({
  type: z.literal('img'),
  attr: z.object({
    src: z.string(),
  }),
});

const CAPTION_MAX_LENGTH = 100;

export const figcaptionSchema = z
  .string()
  .max(CAPTION_MAX_LENGTH)
  .transform(stripLineBreaks);

const figureFigcaptionSchema = z.object({
  type: z.literal('figcaption'),
  content: z.tuple([
    z.object({
      type: z.literal('text'),
      text: figcaptionSchema,
    }),
  ]),
});

const figureSchema = z.object({
  type: z.literal('figure'),
  content: z.tuple([figureImageSchema, figureFigcaptionSchema]),
});

export const storyImageSchema = z.object({
  type: z.literal('doc'),
  content: z.tuple([figureSchema]),
});

export const storyEditorSchemaMap = {
  PARAGRAPH: storyParagraphSchema,
  H1: storyH1Schema,
  H2: storyH2Schema,
  BLOCKQUOTE: storyBlockquoteSchema,
} as const;

export const storyVideoUrlSchema = z.string().regex(YOUTUBE_EMBED_REGEX);

export const storyVideoContentSchema = z.object({
  type: z.literal('youtube'),
  attrs: z.object({
    src: storyVideoUrlSchema,
    width: z.number().int(),
    height: z.number().int(),
  }),
});

export const storyVideoSchema = z.object({
  type: z.literal('doc'),
  content: z.tuple([storyVideoContentSchema]),
});

export const storyContentTypeSchemaMap = {
  ...storyEditorSchemaMap,
  IMAGE: storyImageSchema,
  /**
   * TODO (storytelling): add content type schemas
   */
  NFT: z.object({
    type: z.literal('doc'),
    content: z.tuple([]),
  }),
  VIDEO: z.object({
    type: z.literal('doc'),
    content: z.tuple([]),
  }),
} as const;

const youtubeEmbedUrlSchema = z.string().transform((url) => {
  const match = VIDEO_ID_REGEX.exec(url);

  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  } else {
    throw new Error('Invalid YouTube URL');
  }
});

export const youtubeUrlSchema = z
  .string()
  .regex(YOUTUBE_REGEX)
  .transform(async (url) => {
    /**
     * TODO (storytelling): abstract embed support
     */
    const res = await fetch('https://noembed.com/embed?url=' + url);
    const data = await res.json();

    const widthHeightSchema = z.object({
      width: z.number().int(),
      height: z.number().int(),
    });

    const youtubeEmbedUrl = youtubeEmbedUrlSchema.parse(url);

    return {
      ...widthHeightSchema.parse(data),
      src: youtubeEmbedUrl,
      state: 'POPULATED' as const,
    };
  });
